import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useRoles, useRolePermissions } from '../../hooks/useRoles';
import { useTeams, formatTeamName } from '../../hooks/useTeams';
import { useContexts } from '../../hooks/useContexts';
import { usePermissions } from '../../hooks/usePermissions';
import { useTeamMembers } from '../../hooks/useTeamMembers';
import { useContextTeams, formatTeamWithRolesName } from '../../hooks/useContextTeams';
import { useDivisions } from '../../hooks/useDivisions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const formatAppliesTo = (appliesTo: string): string => {
  const specialCases: Record<string, string> = {
    cm: 'Change Management',
    org: 'Organization',
    div: 'Division',
    gateway_provisioning_token: 'Gateway Provisioning Token',
    gateway_provisioning_tokens: 'Gateway Provisioning Tokens',
    api_key: 'API Key',
    api_keys: 'API Keys',
  };
  const lowerCase = appliesTo.toLowerCase();
  if (specialCases[lowerCase]) return specialCases[lowerCase];
  return appliesTo
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const TeamMembersDisplay: React.FC<{ teamId: string }> = ({ teamId }) => {
  const { data: teamMembers, isLoading, error } = useTeamMembers(teamId);

  if (isLoading) {
    return <div className="pl-6 pt-3"><Progress value={undefined} className="h-1" /></div>;
  }
  if (error) {
    return (
      <div className="pl-6 pt-3">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading members: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!teamMembers || teamMembers.length === 0) {
    return (
      <div className="pl-6 pt-3">
        <p className="text-xs text-muted-foreground">This team has no members.</p>
      </div>
    );
  }

  return (
    <div className="pl-6 pt-3">
      <div className="rounded-md border border-border-subtle bg-muted/30 p-3">
        <p className="text-sm font-semibold mb-2 flex items-center gap-2">
          <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
            {teamMembers.length}
          </Badge>
          {teamMembers.length === 1 ? 'member' : 'members'}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {teamMembers.map((member) => (
                <tr key={member.id}>
                  <td className="px-3 py-2">
                    {member.firstName} {member.lastName}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {member.email}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ContextTeamRolesDisplay: React.FC<{ contextId: string }> = ({ contextId }) => {
  const { data: contextTeams, isLoading, error } = useContextTeams(contextId);

  if (isLoading) {
    return <div className="pl-6 pt-3"><Progress value={undefined} className="h-1" /></div>;
  }
  if (error) {
    return (
      <div className="pl-6 pt-3">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading team assignments:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!contextTeams || contextTeams.length === 0) {
    return (
      <div className="pl-6 pt-3">
        <p className="text-xs text-muted-foreground">
          No teams have been assigned roles in this context yet.
        </p>
      </div>
    );
  }

  return (
    <div className="pl-6 pt-3">
      <div className="rounded-md border border-border-subtle bg-muted/30 p-3">
        <p className="text-sm font-semibold mb-2 flex items-center gap-2">
          <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
            {contextTeams.length}
          </Badge>
          {contextTeams.length === 1 ? 'team' : 'teams'} with role assignments
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium">Team</th>
                <th className="px-3 py-2 font-medium">Roles</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {contextTeams.map((teamWithRoles) => (
                <tr key={teamWithRoles.teamId}>
                  <td className="px-3 py-2 font-medium">
                    {formatTeamWithRolesName(teamWithRoles)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {teamWithRoles.roles.map((role) => (
                        <Badge
                          key={role.id}
                          className="bg-sev-5/15 text-sev-5 border-0 font-normal"
                        >
                          {role.name}
                        </Badge>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const RolePermissionsDisplay: React.FC<{ roleId: string }> = ({ roleId }) => {
  const { data: rolePermissions, isLoading, error } = useRolePermissions(roleId);

  if (isLoading) {
    return <div className="pl-6 pt-3"><Progress value={undefined} className="h-1" /></div>;
  }
  if (error) {
    return (
      <div className="pl-6 pt-3">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading permissions:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!rolePermissions || rolePermissions.length === 0) {
    return (
      <div className="pl-6 pt-3">
        <p className="text-xs text-muted-foreground">
          This role has no permissions assigned.
        </p>
      </div>
    );
  }

  const groupedPerms = rolePermissions.reduce<
    Partial<Record<string, typeof rolePermissions>>
  >((acc, perm) => {
    if (!acc[perm.appliesTo]) acc[perm.appliesTo] = [];
    acc[perm.appliesTo]?.push(perm);
    return acc;
  }, {});

  return (
    <div className="pl-6 pt-3">
      <div className="rounded-md border border-border-subtle bg-muted/30 p-3 space-y-3">
        <p className="text-sm font-semibold flex items-center gap-2">
          <Badge className="bg-sev-5/15 text-sev-5 border-0 font-normal">
            {rolePermissions.length}
          </Badge>
          permissions assigned to this role
        </p>
        {Object.entries(groupedPerms)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([appliesTo, perms]) => {
            if (!perms) return null;
            return (
              <div key={appliesTo}>
                <p className="text-xs font-semibold mb-1.5">
                  {formatAppliesTo(appliesTo)} ({perms.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {perms.map((perm) => (
                    <Badge
                      key={perm.id}
                      variant="secondary"
                      className="font-normal"
                      title={perm.description || perm.name}
                    >
                      <code className="font-mono text-xs">{perm.name}</code>
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

interface ExpandableRowProps {
  expanded: boolean;
  onToggle: () => void;
  label: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
}

function ExpandableRow({
  expanded,
  onToggle,
  label,
  description,
  children,
}: ExpandableRowProps) {
  return (
    <div className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left rounded-md bg-muted/40 hover:bg-muted/60 p-3"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
          {label}
        </div>
        {description ? (
          <p className="text-xs text-muted-foreground mt-2 pl-6">{description}</p>
        ) : null}
      </button>
      {expanded ? children : null}
    </div>
  );
}

const PermissionsOverview: React.FC = () => {
  const { user } = useAuth();
  const organizationId = user?.organization?.id;

  const { data: roles, isLoading: rolesLoading } = useRoles(organizationId);
  const { data: teams, isLoading: teamsLoading } = useTeams(organizationId);
  const { data: contexts, isLoading: contextsLoading } = useContexts(organizationId);
  const { data: permissions, isLoading: permissionsLoading } = usePermissions();
  const { data: divisions } = useDivisions(organizationId);

  const [selectedView, setSelectedView] = useState<
    'roles' | 'teams' | 'contexts' | 'permissions'
  >('roles');
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const [expandedPermissions, setExpandedPermissions] = useState<Set<string>>(new Set());
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const [expandedContexts, setExpandedContexts] = useState<Set<string>>(new Set());

  const isLoading = rolesLoading || teamsLoading || contextsLoading || permissionsLoading;

  const toggle = (set: Set<string>, key: string, fn: (s: Set<string>) => void) => {
    const ne = new Set(set);
    if (ne.has(key)) ne.delete(key);
    else ne.add(key);
    fn(ne);
  };

  const getDivisionName = (divisionId: string | null): string => {
    if (!divisionId) return 'Organization';
    const division = divisions?.find((d) => d.id === divisionId);
    return division ? division.name : 'Unknown Division';
  };

  const groupedPermissions = permissions?.reduce<
    Partial<Record<string, typeof permissions>>
  >((acc, permission) => {
    if (!acc[permission.appliesTo]) acc[permission.appliesTo] = [];
    acc[permission.appliesTo]?.push(permission);
    return acc;
  }, {});

  return (
    <PageContainer>
      <PageHeader
        title="Permissions overview"
        subtitle="Visualize how permissions flow through roles, teams, and contexts"
      />

      {isLoading ? (
        <div className="rounded-lg border border-border bg-card p-4">
          <Progress value={undefined} className="h-1" />
        </div>
      ) : (
        <>
          <Tabs
            value={selectedView}
            onValueChange={(v) => { setSelectedView(v as typeof selectedView); }}
            className="mb-4"
          >
            <div className="max-w-full overflow-x-auto">
              <TabsList className="w-max">
                <TabsTrigger value="permissions" className="gap-2">
                  Permissions
                  <Badge variant="secondary" className="font-normal">
                    {permissions?.length || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="roles" className="gap-2">
                  Roles
                  <Badge variant="secondary" className="font-normal">
                    {roles?.length || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="teams" className="gap-2">
                  Teams
                  <Badge variant="secondary" className="font-normal">
                    {teams?.length || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="contexts" className="gap-2">
                  Contexts
                  <Badge variant="secondary" className="font-normal">
                    {contexts?.length || 0}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="permissions">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="text-sm font-semibold mb-1">All permissions</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Permissions are grouped by the resource type they apply to.
                </p>
                {groupedPermissions
                  ? Object.entries(groupedPermissions)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([appliesTo, perms]) => {
                        if (!perms) return null;
                        const isExpanded = expandedPermissions.has(appliesTo);
                        return (
                          <ExpandableRow
                            key={appliesTo}
                            expanded={isExpanded}
                            onToggle={() => {
                              toggle(expandedPermissions, appliesTo, setExpandedPermissions);
                            }}
                            label={
                              <>
                                <strong>{formatAppliesTo(appliesTo)}</strong>
                                <Badge variant="secondary" className="ml-2 font-normal">
                                  {perms.length} permissions
                                </Badge>
                              </>
                            }
                          >
                            <div className="pl-6 pt-3">
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="border-b border-border-subtle">
                                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                                      <th className="px-3 py-2 font-medium">Permission</th>
                                      <th className="px-3 py-2 font-medium">Description</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border-subtle">
                                    {perms.map((permission) => (
                                      <tr key={permission.id}>
                                        <td className="px-3 py-2">
                                          <code className="font-mono text-xs">
                                            {permission.name}
                                          </code>
                                        </td>
                                        <td className="px-3 py-2 text-xs text-muted-foreground">
                                          {permission.description || 'No description'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </ExpandableRow>
                        );
                      })
                  : null}
              </div>
            </TabsContent>

            <TabsContent value="roles">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="text-sm font-semibold mb-1">Roles and their permissions</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Expand each role to see its assigned permissions.
                </p>
                {roles && roles.length > 0 ? (
                  roles.map((role) => {
                    const isExpanded = expandedRoles.has(role.id);
                    return (
                      <ExpandableRow
                        key={role.id}
                        expanded={isExpanded}
                        onToggle={() => {
                          toggle(expandedRoles, role.id, setExpandedRoles);
                        }}
                        label={
                          <>
                            <strong>{role.name}</strong>
                            <Badge
                              className={
                                role.isGlobal
                                  ? 'ml-2 bg-sev-3/15 text-foreground border-0 font-normal'
                                  : 'ml-2 bg-sev-4/15 text-sev-4 border-0 font-normal'
                              }
                            >
                              {role.isGlobal ? 'System default' : 'Custom'}
                            </Badge>
                            {!role.isMutable ? (
                              <Badge variant="secondary" className="ml-1 font-normal">
                                Read-only
                              </Badge>
                            ) : null}
                          </>
                        }
                        description={role.description || undefined}
                      >
                        <RolePermissionsDisplay roleId={role.id} />
                      </ExpandableRow>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No roles found in this organization.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="teams">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="text-sm font-semibold mb-1">Teams and their members</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Expand each team to see its members.
                </p>
                {teams && teams.length > 0 ? (
                  teams.map((team) => {
                    const isExpanded = expandedTeams.has(team.id);
                    return (
                      <ExpandableRow
                        key={team.id}
                        expanded={isExpanded}
                        onToggle={() => {
                          toggle(expandedTeams, team.id, setExpandedTeams);
                        }}
                        label={
                          <>
                            <strong>{formatTeamName(team)}</strong>
                            <Badge
                              className={
                                team.isImmutable
                                  ? 'ml-2 bg-sev-3/15 text-foreground border-0 font-normal'
                                  : 'ml-2 bg-sev-4/15 text-sev-4 border-0 font-normal'
                              }
                            >
                              {team.isImmutable ? 'SSO group' : 'Custom team'}
                            </Badge>
                          </>
                        }
                        description={team.description || undefined}
                      >
                        <TeamMembersDisplay teamId={team.id} />
                      </ExpandableRow>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No teams found in this organization.
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="contexts">
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="text-sm font-semibold mb-1">Contexts and team-role assignments</h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Expand each context to see which teams have been assigned roles.
                </p>
                {contexts && contexts.length > 0 ? (
                  contexts.map((context) => {
                    const isExpanded = expandedContexts.has(context.id);
                    return (
                      <ExpandableRow
                        key={context.id}
                        expanded={isExpanded}
                        onToggle={() => {
                          toggle(expandedContexts, context.id, setExpandedContexts);
                        }}
                        label={
                          <>
                            <strong>{context.name}</strong>
                            <Badge
                              className={
                                context.divisionId
                                  ? 'ml-2 bg-sev-5/15 text-sev-5 border-0 font-normal'
                                  : 'ml-2 bg-sev-4/15 text-sev-4 border-0 font-normal'
                              }
                            >
                              {getDivisionName(context.divisionId)}
                            </Badge>
                          </>
                        }
                      >
                        <ContextTeamRolesDisplay contextId={context.id} />
                      </ExpandableRow>
                    );
                  })
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No contexts found in this organization.
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <Alert className="border-sev-5/30 bg-sev-5/5">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <p className="font-semibold mb-2">How permissions work</p>
              <ol className="list-decimal pl-6 space-y-1 text-xs">
                <li>
                  <strong>Permissions</strong> define specific actions (e.g.,{' '}
                  <code className="font-mono">device:create</code>,{' '}
                  <code className="font-mono">role:read</code>)
                </li>
                <li>
                  <strong>Roles</strong> are collections of permissions (Administrator,
                  Viewer, Operator)
                </li>
                <li>
                  <strong>Teams</strong> are groups of users
                </li>
                <li>
                  <strong>Contexts</strong> are permission scopes at Organization or
                  Division level
                </li>
                <li>
                  <strong>User permissions</strong> = Union of all permissions from all
                  roles of all teams the user belongs to, in their active context
                </li>
              </ol>
              <p className="text-xs mt-2">
                <strong>Permission flow:</strong> Permissions → Roles → Teams (in
                Contexts) → Users
              </p>
            </AlertDescription>
          </Alert>
        </>
      )}
    </PageContainer>
  );
};

export default PermissionsOverview;
